import { Box, Flex, Text, useT } from '@minetrace/ui'
import { LuFileScan, LuMonitorCog, LuTriangleAlert } from 'react-icons/lu'
import {
  INDICE_MODO_DIGITALIZADO_SIN_FEDATARIO,
  SERIE_SIN_CLASIFICAR,
} from '../../../api/models/response/indiceDigital'
import { m3, themeColors } from '../../../theme'
import { Hint } from './ui'

/**
 * Serie documental de una entrada. `MT-99 Sin clasificar` es el centinela del
 * catálogo: significa que nadie le ha asignado serie al tipo de ítem, y el
 * D.S. 029-2021-PCM art. 40.6 exige clasificar. ESO SÍ es un aviso —es una laguna
 * real del expediente— y va en ámbar, a diferencia de "el expediente ha crecido".
 */
export function SerieBadge({ codigo, nombre }: { codigo: string; nombre: string }) {
  const { t } = useT()
  const unclassified = codigo === SERIE_SIN_CLASIFICAR

  const badge = (
    <Flex
      alignItems="center"
      gap={1.5}
      px={2}
      py={0.5}
      borderRadius="md"
      bg={unclassified ? 'rgba(234, 179, 8, 0.16)' : themeColors.bgHover}
      color={unclassified ? '#946200' : themeColors.textSecondary}
      maxW="100%"
    >
      {unclassified && <LuTriangleAlert size={11} />}
      <Text fontSize="10px" fontWeight="700" fontFamily="monospace" flexShrink={0}>{codigo}</Text>
      <Text fontSize="10px" fontWeight="600" truncate>{nombre}</Text>
    </Flex>
  )

  if (!unclassified) return badge
  return <Hint label={t('expedientes.electronico.serie.unclassifiedTooltip')}>{badge}</Hint>
}

/**
 * Columna "Origen" (modo de incorporación, art. 48). VA EN LA TABLA, no escondida en
 * el detalle: es la diferencia entre un documento que generó el sistema y una copia
 * digitalizada que NO pasó por fedatario juramentado, y quien lee el índice tiene que
 * poder verla de un vistazo. El tooltip lo dice con todas las letras.
 */
export function OrigenBadge({ modo }: { modo: number }) {
  const { t } = useT()
  const isCopy = modo === INDICE_MODO_DIGITALIZADO_SIN_FEDATARIO

  return (
    <Hint
      label={t(isCopy
        ? 'expedientes.electronico.origen.copiaTooltip'
        : 'expedientes.electronico.origen.nativoTooltip')}
    >
      <Flex
        alignItems="center"
        gap={1.5}
        px={2}
        py={0.5}
        borderRadius="md"
        border="1px solid"
        borderColor={isCopy ? themeColors.borderHover : themeColors.border}
        bg={isCopy ? themeColors.bgHover : 'transparent'}
        color={isCopy ? themeColors.textSecondary : themeColors.textMuted}
      >
        {isCopy ? <LuFileScan size={11} /> : <LuMonitorCog size={11} />}
        <Text fontSize="10px" fontWeight="700">
          {t(isCopy ? 'expedientes.electronico.origen.copia' : 'expedientes.electronico.origen.nativo')}
        </Text>
        <Box
          as="span"
          w="4px"
          h="4px"
          borderRadius="full"
          bg={isCopy ? m3.warning : m3.outlineVariant}
        />
      </Flex>
    </Hint>
  )
}
